import { getDownloadUrl } from './download-url'
import { getImageUrl } from './image-url'

export type LightboxImageOptions = {
  freeDownloadsEnabled: boolean
  highResDownloadsEnabled: boolean
  highResDownloadMode: 'watermarked' | 'original'
  freeDownloadMaxWidth: number
  downloadVersion: string
  shareToken?: string
  /** Saved favorites-list token that opens a private photo. */
  favoriteToken?: string
}

/** Width of the unwatermarked lightbox preview, served by /api/image. Upload pre-generates it for galleries that use it. */
export const UNWATERMARKED_PREVIEW_WIDTH = 1600

export function getPhotoDownloadVersion(photo: { version?: string }, downloadVersion: string) {
  return photo.version ? `${downloadVersion}-${photo.version}` : downloadVersion
}

/** Watermarked by default; the original preview only when an album offers original-only downloads. */
export function usesWatermarkedPreview(options: Pick<LightboxImageOptions, 'freeDownloadsEnabled' | 'highResDownloadsEnabled' | 'highResDownloadMode'>) {
  return options.freeDownloadsEnabled || options.highResDownloadMode === 'watermarked' || !options.highResDownloadsEnabled
}

/** usesWatermarkedPreview for a gallery's own download settings, mapped the way the gallery page maps them. */
export function galleryUsesWatermarkedPreview(gallery: {
  allowWatermarkedDownloads: boolean
  allowOriginalDownloads: boolean
  originalDownloadMode: 'watermarked' | 'original'
}) {
  return usesWatermarkedPreview({
    freeDownloadsEnabled: gallery.allowWatermarkedDownloads,
    highResDownloadsEnabled: gallery.allowOriginalDownloads,
    highResDownloadMode: gallery.originalDownloadMode,
  })
}

/** Widest the lightbox preview can be, matching the server's clamp in the download handler. */
export function getLightboxPreviewWidth(options: Pick<LightboxImageOptions, 'freeDownloadsEnabled' | 'highResDownloadsEnabled' | 'highResDownloadMode' | 'freeDownloadMaxWidth'>) {
  return usesWatermarkedPreview(options)
    ? Math.max(400, Math.min(options.freeDownloadMaxWidth, 2400))
    : UNWATERMARKED_PREVIEW_WIDTH
}

/** The one URL the lightbox shows for a photo. Preloads must use this exact URL to hit the browser cache. */
export function getLightboxPreviewUrl(photo: { filename: string; version?: string }, options: LightboxImageOptions) {
  const v = getPhotoDownloadVersion(photo, options.downloadVersion)
  return usesWatermarkedPreview(options)
    ? getDownloadUrl(photo.filename, {
      preview: 'true',
      w: String(options.freeDownloadMaxWidth),
      v,
      share: options.shareToken,
      fav: options.favoriteToken,
    })
    : getImageUrl(photo.filename, UNWATERMARKED_PREVIEW_WIDTH, { v, share: options.shareToken, fav: options.favoriteToken })
}

/** The free watermarked download (a JPEG attachment). Previews are WebP, so downloads must not reuse the preview URL. */
export function getWatermarkedDownloadUrl(
  photo: { filename: string; version?: string },
  options: Pick<LightboxImageOptions, 'freeDownloadMaxWidth' | 'downloadVersion' | 'shareToken' | 'favoriteToken'>
) {
  return getDownloadUrl(photo.filename, {
    w: String(options.freeDownloadMaxWidth),
    v: getPhotoDownloadVersion(photo, options.downloadVersion),
    share: options.shareToken,
    fav: options.favoriteToken,
  })
}
